import * as React from 'react';
import cn from 'classnames';
import { Popover as BasePopover, PopoverDisclosure, PopoverArrow, usePopoverState, PopoverDisclosureProps } from 'reakit/Popover';
import { Placement } from '../../types';
import './Popover.css';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef';

export type PopoverRenderProps = {
    /** Hides the popover */
    hide: () => void;
};

export type PopoverChildren = Omit<React.ReactNode, 'Function'> | ((props: PopoverRenderProps) => React.ReactNode);

export type PopoverProps = React.HTMLAttributes<HTMLDivElement> & {
    /** Content can be any valid react node, for e.g. a `div`, or a render function that is given the `hide` property */
    children: PopoverChildren;
    /** Defines the component that controls visibility of the modal */
    disclosure?: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
    /**
     * Prevents opening of popover on disclosure click.
     * We provide the option to disable the on click due to the hanger component using popover.
     * Default value is `false`
     */
    /** @internal */
    disableOpenOnClick?: boolean;
    /**
     * Close the popover when clicking outside it.
     * Default value is `true`
     */
    hideOnClickOutside?: boolean;
    /**
     * When `true`, popover will be treated as a `modal`: is rendered within a `portal`,
     * focus is trapped within the dialog and scrolling on `body` is prevented when popover is visible.
     * If false, scrolling on body will work even if the popover is visible, and focus is free.
     * Default value is `true`
     */
    modal?: boolean;
    /**
     * Set the position of the popover relative to its disclosure.
     * Default value is `bottom`
     */
    placement?: Placement;
    /**
     * Set whether the popover is visible.
     * Default value is `false`
     */
    visible?: boolean;
};

export const Popover = React.forwardRef((props: PopoverProps, ref: React.Ref<HTMLDivElement>) => {
    const {
        children,
        disclosure,
        disableOpenOnClick = false,
        modal = true,
        placement = 'bottom',
        hideOnClickOutside = true,
        visible = false,
        ...otherProps
    } = props;
    const internalRef = useProxiedRef<HTMLDivElement>(ref);
    const defaultRef = React.useRef(typeof document !== 'undefined' ? document.body : null);
    const className = cn('yt-popover', props.className);
    const popover = usePopoverState({ visible, placement });

    return (
        <>
            {disclosure && (
                <PopoverDisclosure {...popover}>
                    {({ type: _, onClick, ...disclosureProps }: PopoverDisclosureProps) => {
                        return React.cloneElement(React.Children.only(disclosure), {
                            ...disclosureProps,
                            // DOL: We provide the option to disable the on click due to the hanger component using popover.
                            //      The hanger displays a popover on load. But when closed it should remain so. Since the
                            //      hanger uses popover, the onClick functionality of the element is added to allow
                            //      the popover to reopen - We want to prevent this. This will disable any onClick on the disclosure,
                            //      but since the hanger is not to be attached to any disclousure with an onClick functionality,
                            //      this method was decided as being sufficient
                            onClick: disableOpenOnClick ? undefined : onClick,
                        });
                    }}
                </PopoverDisclosure>
            )}
            {popover.visible && (
                <BasePopover
                    {...otherProps}
                    {...popover}
                    className={className}
                    hideOnClickOutside={hideOnClickOutside}
                    modal={modal}
                    ref={internalRef}
                    unstable_finalFocusRef={disclosure ? undefined : defaultRef}>
                    <PopoverArrow {...popover} className="yt-popover__arrow" />
                    {typeof children === 'function' ? children({ hide: popover.hide }) : children}
                </BasePopover>
            )}
        </>
    );
});
